import { AddOrganizationDomainRequestDto, DomainCryptoResponseDto, OrganizationDomainResponseDto, UpdateVerifiedDomainConfigRequestDto, VerifiedDomainConfigResponseDto, } from '@lucidlink/dto/web-service-api';
import { NotFoundError } from '#fetcher/fetch-error.types';
import { arrayParserFromDto, parserFromDto } from '#fetcher/parse-utils.ts';
import { prepareDomainCryptoForCreate, prepareDomainCryptoForExport, prepareDomainCryptoForImport, } from './prepare-domain-crypto';
export const createDomainManager = ({ workspaceFetcher, loginToWorkspace, getAdminCrypto, }) => {
    return new DomainManagerImpl(workspaceFetcher, loginToWorkspace, getAdminCrypto);
};
class DomainManagerImpl {
    workspaceFetcher;
    loginToWorkspace;
    getAdminCrypto;
    constructor(workspaceFetcher, loginToWorkspace, getAdminCrypto) {
        this.workspaceFetcher = workspaceFetcher;
        this.loginToWorkspace = loginToWorkspace;
        this.getAdminCrypto = getAdminCrypto;
    }
    async listDomains() {
        await this.loginToWorkspace();
        await this.assertIsAdmin();
        return this.workspaceFetcher.get('domains', {
            parser: arrayParserFromDto(OrganizationDomainResponseDto),
        });
    }
    async addDomain(domainName) {
        await this.loginToWorkspace();
        await this.assertIsAdmin();
        return this.workspaceFetcher.post('domains', {
            body: AddOrganizationDomainRequestDto.create({
                organizationDomain: domainName,
            }),
            parser: parserFromDto(OrganizationDomainResponseDto),
        });
    }
    async removeDomain(domainId) {
        await this.loginToWorkspace();
        await this.assertIsAdmin();
        await this.workspaceFetcher.delete(['domains', domainId]);
    }
    async startVerification(domainId) {
        await this.loginToWorkspace();
        await this.assertIsAdmin();
        return this.workspaceFetcher.put(['domains', domainId, 'verification'], {
            parser: parserFromDto(OrganizationDomainResponseDto),
        });
    }
    async updateSsoEnforcement(domainId, ssoEnforcementType) {
        await this.loginToWorkspace();
        await this.assertIsAdmin();
        return this.workspaceFetcher.patch(['domains', domainId, 'verified-config'], {
            body: UpdateVerifiedDomainConfigRequestDto.create({
                ssoEnforcementType,
            }),
            parser: parserFromDto(VerifiedDomainConfigResponseDto),
        });
    }
    async updateRemapParentDomainEmails(domainId, remapParentDomainEmails) {
        await this.loginToWorkspace();
        await this.assertIsAdmin();
        return this.workspaceFetcher.patch(['domains', domainId, 'verified-config'], {
            body: UpdateVerifiedDomainConfigRequestDto.create({
                remapParentDomainEmails,
            }),
            parser: parserFromDto(VerifiedDomainConfigResponseDto),
        });
    }
    async exportDomainSecret(domainId) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        try {
            return await this.fetchDomainKey(adminKeys, domainId);
        }
        catch (e) {
            if (e instanceof NotFoundError) {
                await this.createCrypto(domainId);
            }
            else {
                throw e;
            }
        }
        return this.fetchDomainKey(adminKeys, domainId);
    }
    async importDomainSecret(domainId, domainKey) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        const ssoCrypto = await adminCrypto.getSsoCrypto();
        const ssoSecretKey = await ssoCrypto.verifyAndGetSsoSecretKey();
        const domainCryptoDto = await prepareDomainCryptoForImport({
            adminKeys,
            domainKey,
            ssoSecretKey,
        });
        await this.workspaceFetcher.post(['domains', domainId, 'crypto'], {
            body: domainCryptoDto,
            queryParams: { isImporting: 'true' },
        });
    }
    async createCrypto(domainId) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        const ssoCrypto = await adminCrypto.getSsoCrypto();
        const ssoSecretKey = await ssoCrypto.verifyAndGetSsoSecretKey();
        const domainCryptoDto = await prepareDomainCryptoForCreate({
            adminKeys,
            ssoSecretKey,
        });
        await this.workspaceFetcher.post(['domains', domainId, 'crypto'], {
            body: domainCryptoDto,
        });
    }
    async fetchDomainKey(adminKeys, domainId) {
        const domainCryptoData = await this.workspaceFetcher.get(['domains', domainId, 'crypto'], {
            parser: parserFromDto(DomainCryptoResponseDto),
        });
        return prepareDomainCryptoForExport({
            adminKeys,
            domainCryptoData,
        });
    }
    assertIsAdmin = async () => {
        await this.getAdminCrypto();
    };
}
